import Modal from 'components/ui-kit/Modal/Modal';
import { SHOP } from 'config/fe-routes';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShopFeedbackService from 'services/shop-feedback.service';
import { redirectTo } from 'utils/route-helpers';
import { useNotification } from 'utils/snackbar';

import ConfirmActions from './ConfirmActions';
import ConfirmView from './ConfirmView';
import FeedbackActions from './FeedbackActions';
import FeedbackView from './FeedbackView';
import RateActions from './RateActions';
import RateView from './RateView';

enum View {
  CONFIRM = 'CONFIRM',
  RATE = 'RATE',
  REVIEW = 'REVIEW',
}

const shopFeedbackService = new ShopFeedbackService();

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onFeedbackSubmitted?: () => void;
  shopId?: number;
  orderId?: number;
  sellerName?: string;
  productImage?: string;
  ratingOnly?: boolean;
};

const ItemReceivedConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  onFeedbackSubmitted,
  shopId,
  orderId,
  sellerName,
  productImage,
  ratingOnly,
}: Props) => {
  const { t } = useTranslation();
  const { showSuccessNotification } = useNotification();
  const [view, setView] = useState<View>(View.CONFIRM);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setView(ratingOnly ? View.RATE : View.CONFIRM);
      setRating(0);
      setHovered(0);
      setFeedback('');
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm();
    setView(View.RATE);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      await shopFeedbackService.addShopFeedback(shopId, { orderId, rating, review: feedback });
      onFeedbackSubmitted?.();
      showSuccessNotification(t('components.feedback.success'));
      redirectTo({ ...SHOP(sellerName), query: { tab: 'reviews' } });
      onClose();
    } catch (err) {
      if (err?.data?.data?.errorCode === 'VALIDATION_ERROR' && !!err?.data?.data?.errors?.rating) {
        setError(t('components.feedback.errors.missing-rating'));
      } else {
        setError(err?.data?.data?.msg || t('common.general-error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="item-received-confirmation-modal"
      title={view === View.CONFIRM ? t('pages.purchases.content-native') : undefined}
      actions={
        view === View.CONFIRM ? (
          <ConfirmActions onClose={onClose} onConfirm={handleConfirm} />
        ) : view === View.RATE ? (
          <RateActions rating={rating} onNext={() => setView(View.REVIEW)} />
        ) : (
          <FeedbackActions feedback={feedback} submitting={submitting} onSubmit={handleSubmit} />
        )
      }
    >
      {view === View.CONFIRM && <ConfirmView />}

      {view === View.RATE && (
        <RateView
          productImage={productImage}
          sellerName={sellerName}
          rating={rating}
          hovered={hovered}
          setRating={setRating}
          setHovered={setHovered}
        />
      )}

      {view === View.REVIEW && (
        <FeedbackView
          productImage={productImage}
          sellerName={sellerName}
          feedback={feedback}
          setFeedback={setFeedback}
          error={error}
          onBack={() => setView(View.RATE)}
        />
      )}
    </Modal>
  );
};

export default ItemReceivedConfirmationModal;
